/**
 * @module app.interaction.Track
 */
import olEventsEvent from 'ol/events/Event.js';
import olFeature from 'ol/Feature.js';
import {distance as olDistance} from 'ol/coordinate.js';
import olGeomLineString from 'ol/geom/LineString.js';
import olGeomPoint from 'ol/geom/Point.js';
import olInteractionPointer from 'ol/interaction/Pointer.js';
import olLayerVector from 'ol/layer/Vector.js';
import olSourceVector from 'ol/source/Vector.js';
import {
  Fill as olStyleFill, Text as olStyleText,
  RegularShape as olStyleRegularShape, Stroke as olStyleStroke, Style as olStyleStyle} from 'ol/style';
import {parseGraphhopperPoints} from '../graphhopperutil.js';
import {colorWithOpacity, lonLatToMapProjection, mapProjectionToLonLat} from "../utils";
import {MAP_PROJECTION, TRACK_WIDTH, Z_INDEX} from "../constants";

/**
 * Base interaction to draw or modify a track.
 */
class Track extends olInteractionPointer {
  constructor(options) {
    super({
      handleDownEvent: options.handleDownEvent,
      handleDragEvent: options.handleDragEvent,
      handleEvent: options.handleEvent,
      handleMoveEvent: options.handleMoveEvent,
      handleUpEvent: options.handleUpEvent
    });

    /**
     * Request elevation data?
     * @type {boolean}
     */
    this.elevation = options.elevation;

    // Use the route endpoint instead of the nearest endpoint for snapping
    this.useRouteForSnapping = false;

    /**
     * Should the points and segments be snapped?
     * @type {boolean}
     */
    this.snap = options.snap;

    /**
     * If the distance between the original point and the mapped point
     * on the network is less than this tolerance, the point is snapped.
     * @type {number}
     */
    this.snapTolerance = options.snapTolerance !== undefined ? options.snapTolerance : 15;

    /**
     * @type {string}
     */
    this.graphhopperUrl = options.graphhopperUrl;

    /**
     * @type {string}
     */
    this.routingProfile = options.routingProfile;

    /**
     * @type {string}
     */
    this.graphhopperLocateUrl = options.graphhopperUrl + '/nearest?elevation=true&point={point}';

    /**
     * @type {function}
     */
    this.requestInfoForGeom = options.requestInfoForGeom;

    /**
     * @type {string}
     */
    this.graphhopperRoutingUrl = '&point={from}&point={to}' +
        '&type=json&elevation={elevation}' +
        '&instructions=false&points_encoded=true';

    let color = options.color;
    let lineColor = color;
    let opacity = 1;
    let fillColor = colorWithOpacity(color, 0.5);
    const textFillWhite = new olStyleFill({color: [255, 255, 255, 1]});
    const textFillBlack = new olStyleFill({color: [0, 0, 0, 1]});
    const defaultStyleCache = {};

    const defaultStyleFn = (feature) => {
      if (this.sketchLineColor) {
        color = this.sketchLineColor.color_rgb || color;
        fillColor = colorWithOpacity(color, 0.5);
        opacity = this.sketchLineColor.opacity;
        lineColor = colorWithOpacity(color, opacity);
      }
      let textStyle = undefined;
      let radius = 8;
      let cacheKey;
      if (feature.getGeometry() instanceof olGeomPoint) {
        const index = this.controlPoints.indexOf(feature);
        let text = `${index + 1}`;
        if (index === 0 || index === this.controlPoints.length - 1) {
          text = index === 0 ? 'A' : 'B';
        }
        cacheKey = `point-${text}-${color}`;
        if (defaultStyleCache[cacheKey]) {
          return defaultStyleCache[cacheKey];
        }
        textStyle = new olStyleText({
          text: text,
          fill: color === '#ffe500' ? textFillBlack : textFillWhite,
        });
        if (index > 98 && index !== this.controlPoints.length - 1) {
          radius = 11;
        }
      } else {
        cacheKey = `segment-${color}-${opacity}`;
        if (defaultStyleCache[cacheKey]) {
          return defaultStyleCache[cacheKey];
        }
      }
      defaultStyleCache[cacheKey] = new olStyleStyle({
        stroke: new olStyleStroke({
          color: lineColor,
          width: TRACK_WIDTH
        }),
        image: new olStyleRegularShape({
          stroke: new olStyleStroke({
            color,
            width: 2
          }),
          fill: new olStyleFill({
            color: fillColor
          }),
          points: 4,
          radius: radius,
          angle: Math.PI / 4
        }),
        text: textStyle
      });
      return [defaultStyleCache[cacheKey]];
    };

    const defaultSketchStyle = new olStyleStyle({
      image: new olStyleRegularShape({
        stroke: new olStyleStroke({
          color,
          width: 2
        }),
        fill: new olStyleFill({
          color: fillColor
        }),
        points: 4,
        radius: 8,
        angle: Math.PI / 4
      })
    });

    this.style = options.style ? () => options.style : defaultStyleFn;
    this.sketchStyle = options.sketchStyle ? options.sketchStyle : [defaultSketchStyle];

    /**
     * The control points of the track.
     * @type {Array.<ol.Feature>}
     */
    this.controlPoints = [];

    /**
     * The segments of the track.
     * @type {import('ol/Feature.js').default[]}
     */
    this.segments = [];

    /**
     * @type {ol.source.Vector}
     */
    this.pointSource = new olSourceVector();

    /**
     * The overlay for the track control points.
     * @type {ol.layer.Vector}
     * @private
     */
    this.pointLayer_ = new olLayerVector({
      'name': 'poi interaction track',
      source: this.pointSource,
      style: this.style,
      zIndex: Z_INDEX.UP
    });

    /**
     * @type {ol.source.Vector}
     */
    this.segmentSource = new olSourceVector();

    /**
     * The overlay for the track segments.
     * @type {ol.layer.Vector}
     * @private
     */
    this.segmentLayer_ = new olLayerVector({
      'name': 'segment interaction track',
      source: this.segmentSource,
      style: this.style,
      zIndex: Z_INDEX.UP
    });
  }

  /**
   *
   */
  clear() {
    this.pointSource.clear();
    this.segmentSource.clear();
    this.controlPoints.length = 0;
    this.segments.length = 0;
  }

  /**
   * Returns a single 'XY' ol.geom.LineString from the segments.
   * @return {ol.geom.LineString}
   */
  getLineString() {
    const coordinates = [];
    this.segments.forEach((segment, i) => {
      const geometry = /** @type {ol.geom.LineString} */ (segment.getGeometry());
      const skipFirstPoint = (i !== 0);
      geometry.getCoordinates().forEach((point, j) => {
        const isFirstPoint = (j === 0);
        if (!(skipFirstPoint && isFirstPoint)) {
          coordinates.push(...point.slice(0, 2)); // actively return XY geometry
        }
      });
    });
    return new olGeomLineString(coordinates, 'XY');
  }

  /**
   * @return {boolean}
   */
  hasHeightProfile() {
    for (let i = 0, ii = this.segments.length; i < ii; i++) {
      const segment = this.segments[i];
      const geometry = /** @type {ol.geom.LineString} */ (segment.getGeometry());
      if (geometry.getStride() != 3) {
        return false;
      }
    }
    return true;
  }

  /**
   * @return {Array.<Array.<number>>}
   */
  async getHeightProfile() {
    let distance = 0;
    const profile = [];
    let previous = null;

    for (let i = 0, ii = this.segments.length; i < ii; i++) {
      const linestring = /** @type {ol.geom.LineString} */ (this.segments[i].getGeometry());
      if (linestring.getStride() != 3) {
        const coords = await this.requestInfoForGeom(linestring);
        if (!coords) {
          return null;
        }
        linestring.setCoordinates(coords);
      }
      const coordinates = linestring.getCoordinates();
      for (let j = 0, jj = coordinates.length; j < jj; j++) {
        const coordinate = coordinates[j];
        if (previous) {
          distance += olDistance(coordinate, previous);
        }
        profile.push([
          coordinate[0],
          coordinate[1],
          coordinate[2],
          distance
        ]);
        previous = coordinate;
      }
    }
    return profile;
  }

  /**
   * @param {ol.geom.Point} originalPoint
   * @param {ol.geom.Point} mappedPoint
   * @return {boolean}
   */
  isValidSnap(originalPoint, mappedPoint) {
    return this.isInTolerance(originalPoint, mappedPoint, this.snapTolerance);
  }

  /**
   * @return {boolean}
   */
  isValidTrack() {
    const cpl = this.controlPoints.length;
    if (this.segments.length === 0 && cpl === 0) {
      return true;
    }
    if (this.segments.length + 1 !== cpl) {
      console.error('CP and segments mismatch', cpl, this.segments.length);
      return false;
    }
    if (this.controlPoints[0].get('segmentIn') || this.controlPoints[cpl - 1].get('segmentOut')) {
      console.error('CP has invalid segments'); // ends should be null
      return false;
    }

    for (let i = 0; i < this.segments.length; ++i) {
      const segment = this.segments[i];
      const coos = segment.getGeometry().getCoordinates();
      for (let j = 0; j < coos.length; ++j) {
        const coo = coos[j];
        if (coo.length > 2 && coo[2] > 5000) { // no mountain above 5k in Switzerland
          console.error(`Segment ${i} has invalid height at ${j} (${coo})`);
          return false;
        }
      }
    }

    /**
     * @param {boolean} prev
     * @param {boolean} curr
     * @return {boolean}
     */
    const reduceBool = function(prev, curr) {
      return prev && curr;
    };

    /**
     * @param {string} i
     * @param {ol.Coordinate} p1
     * @param {ol.Coordinate} p2
     * @return {boolean}
     */
    const xyEqual = function(i, p1, p2) {
      const retx = Math.abs(p1[0] - p2[0]) < 0.1;
      const rety = Math.abs(p1[1] - p2[1]) < 0.1;
      console.assert(retx, 'X not equal at', i, p1[0], 'X', p2[0]);
      console.assert(rety, 'Y not equal at', i, p1[1], 'Y', p2[1]);
      return retx && rety;
    };

    const cp = this.controlPoints;
    const validities = this.segments.map((segment, i) => {
      const linestring = /** @type {ol.geom.LineString} */ (segment.getGeometry());
      const ls1 = linestring.getFirstCoordinate();
      const ls2 = linestring.getLastCoordinate();
      const cp1 = /** @type {ol.geom.Point} */ (cp[i].getGeometry());
      const cp2 = /** @type {ol.geom.Point} */ (cp[i + 1].getGeometry());
      const eq1 = xyEqual(i + 'l', ls1.slice(0, 2), cp1.getCoordinates());
      const eq2 = xyEqual(i + 'r', ls2.slice(0, 2), cp2.getCoordinates());
      return eq1 && eq2;
    });

    const validity = validities.reduce(reduceBool, true);
    return validity;
  }

  /**
   * @param {ol.geom.Point} originalPoint
   * @param {ol.geom.Point} mappedPoint
   * @param {number} tolerance
   * @return {boolean}
   */
  isInTolerance(originalPoint, mappedPoint, tolerance) {
    const map = this.getMap();

    const originalPointPx =
        map.getPixelFromCoordinate(originalPoint.getCoordinates());
    const mappedPointPx =
        map.getPixelFromCoordinate(mappedPoint.getCoordinates());

    const distance = olDistance(originalPointPx, mappedPointPx);

    return distance < tolerance;
  }

  /**
   * @inheritDoc
   */
  setMapWithLayers(map) {
    const prevMap = this.getMap();
    super.setMap(map);

    if (map) {
      map.addLayer(this.segmentLayer_);
      map.addLayer(this.pointLayer_);
    } else if (prevMap) {
      prevMap.removeLayer(this.segmentLayer_);
      prevMap.removeLayer(this.pointLayer_);
    }
  }

  /**
   * @protected
   */
  dispatchChangeEvent() {
    console.assert(this.isValidTrack());
    this.dispatchEvent(new olEventsEvent('trackchanged'));
  }

  /**
   * @protected
   */
  dispatchSegmentChangeEvent() {
    console.assert(this.isValidTrack());
    this.dispatchEvent(new olEventsEvent('segmentchanged'));
  }


  /**
   * Locate the given point on the routing network.
   * @param {ol.Feature} pointToSnap
   * @return {jQuery.Promise} promise resolving to the mapped point geometry
   * @protected
   */
  requestSnapPoint(pointToSnap) {
    const point = /** @type {ol.geom.Point} */ (pointToSnap.getGeometry());
    const position = mapProjectionToLonLat(point.getCoordinates());

    if (this.useRouteForSnapping) {
      // creating a second point one meter away to have a line
      const point2 = new olGeomPoint(point.getFirstCoordinate().map(c => c + 1));
      return this.requestRoute(point, point2).then(
        response => {
          const snappedCoordinate = response.getCoordinates()[1]; // second point of the line is snapped
          return new olGeomPoint(snappedCoordinate);
        }, () => {
          return new olGeomPoint([0, 0]);
        }
      );
    } else {
      const url = this.graphhopperLocateUrl.replace('{point}', position[1] + ',' + position[0]);

      this.dispatchEvent(new olEventsEvent('waitingstarted'));
      const p = fetch(url)
        .then(r => {
          if (r.ok) {
            return r.json()
              .then((response) => {
                if (response.distance === -1) {
                  // for our fork of graphhopper
                  return new olGeomPoint([0, 0]);
                }
                return new olGeomPoint(lonLatToMapProjection(response.coordinates));
              });
          } else {
            return new olGeomPoint([0, 0]);
          }
        });

      const theEnd = () => this.dispatchEvent(new olEventsEvent('waitingfinished'));
      p.then(theEnd, theEnd); // finally is too new to use
      return p;
    }
  }

  /**
   * Request the route between two points for the current profile.
   * @param {ol.geom.Point} pointFrom
   * @param {ol.geom.Point} pointTo
   * @return {jQuery.Promise} promise resolving to the route geometry
   */
  requestRoute(pointFrom, pointTo) {
    const from = mapProjectionToLonLat(pointFrom.getCoordinates());
    const to = mapProjectionToLonLat(pointTo.getCoordinates());

    const url = this.graphhopperUrl + `/route?profile=schm${this.routingProfile}` + this.graphhopperRoutingUrl
      .replace('{elevation}', this.elevation ? 'true' : 'false')
      .replace('{from}', from[1] + ',' + from[0])
      .replace('{to}', to[1] + ',' + to[0]);

    // update application state that we are waiting on a request being finished
    this.dispatchEvent(new olEventsEvent('waitingstarted'));

    return jQuery.getJSON(url).then(response => {
      const route = parseGraphhopperPoints(response['paths'][0]['points'], this.elevation, MAP_PROJECTION);
      return this.wrapRoute(pointFrom, pointTo, route);
    }).always(() => {
      // update application state that the request is finished
      this.dispatchEvent(new olEventsEvent('waitingfinished'));
    });
  }

  /**
   * @param {ol.geom.Point} from
   * @param {ol.geom.Point} to
   * @param {ol.geom.LineString} route
   * @return {ol.geom.LineString}
   */
  wrapRoute(from, to, route) {
    // Routing data gives us a routed segment whose ends does not match the from and
    // to points. So we add them to the route and give them the elevation of
    // the adjacent point.
    const from3 = from.getCoordinates().slice(0, 2);
    from3.push(route.getFirstCoordinate()[2]);
    const to3 = to.getCoordinates().slice(0, 2);
    to3.push(route.getLastCoordinate()[2]);

    // Wrap without copy since getCoordinates returns a new array
    const newRouteCoordinates = route.getCoordinates();
    newRouteCoordinates.push(to3);
    newRouteCoordinates.splice(0, 0, from3);
    newRouteCoordinates.forEach(coos => {
      console.assert(coos.length === 3);
      coos.forEach(c => console.assert(Number.isFinite(c)));
    });
    route.setCoordinates(newRouteCoordinates);
    return route;
  }

  /**
   * @param {ol.Feature} feature
   * @param {string} key
   * @return {ol.Feature}
   * @protected
   */
  getFeatureProp(feature, key) {
    const res = feature.get(key);
    console.assert(res instanceof olFeature);
    return res;
  }

  /**
   * @param {ol.Feature} feature
   * @return {ol.geom.Point}
   * @protected
   */
  getPointGeometry(feature) {
    const res = feature.getGeometry();
    console.assert(res instanceof olGeomPoint);
    return res;
  }
}


/**
 * @typedef {{
 *    color: string,
 *    elevation: boolean,
 *    snap: boolean,
 *    routingProfile: string,
 *    style:(ol.style.Style|Array.<ol.style.Style>|ol.StyleFunction|undefined),
 *    sketchStyle:(ol.style.Style|Array.<ol.style.Style>|ol.StyleFunction|undefined),
 *    handleDownEvent: (function(ol.MapBrowserPointerEvent):boolean|undefined),
 *    handleDragEvent: (function(ol.MapBrowserPointerEvent)|undefined),
 *    handleEvent: (function(ol.MapBrowserEvent):boolean|undefined),
 *    handleMoveEvent: (function(ol.MapBrowserPointerEvent)|undefined),
 *    handleUpEvent: (function(ol.MapBrowserPointerEvent):boolean|undefined)
 * }}
 */
// export const TrackOptions;


export default Track;
